import React, { useEffect } from "react";
import {
  Button,
  Flex,
  SelectField,
  TextAreaField,
  TextField,
  Text,
  Label,
  Divider,
  Heading,
} from "@aws-amplify/ui-react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import FlexFormContainer from "../../common/FlexFormContainer";
import { useUserQuery } from "../../common/useUserQuery";
import { DatePicker } from "rsuite";
import { useCareProvidersQuery } from "../../careProviders/api/useCareProvidersQuery";
import { useAppointmentPostMutation } from "../api/appointments/useAppointmentsMutation";
import { FaRegClock } from "react-icons/fa";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AppointmentEmptyProvider from "./AppointmentEmptyProvider";
import { formatTime } from "../../common/useDateFunctions";

const postRequest = z.object({
  appointmentDate: z.date({ message: "Date is required" }),
  appointmentTime: z.date({ message: "Time is required" }),
  providerUserId: z
    .string({ message: "Provider is Required" })
    .uuid({ message: "Provider is Required" }),
  appointmentFacility: z
    .string({ message: "Facility is Required" })
    .min(1, { message: "Facility is Required" })
    .max(100, { message: "Must be 100 or fewer characters long" }),
  appointmentAddress1: z
    .string({ message: "Address 1 is Required" })
    .min(1, { message: "Address 1 is Required" })
    .max(50, { message: "Must be 50 or fewer characters long" }),
  appointmentAddress2: z.optional(
    z
      .string({ message: "Address 2 is Required" })
      .max(50, { message: "Must be 50 or fewer characters long" })
  ),
  appointmentCity: z
    .string({ message: "City is Required" })
    .min(1, { message: "City is Required" })
    .max(20, { message: "Must be 20 or fewer characters long" }),
  appointmentState: z
    .string({ message: "State is Required" })
    .min(1, { message: "State is Required" })
    .max(20, { message: "Must be 20 or fewer characters long" }),
  appointmentZip: z
    .string({ message: "Zip is Required" })
    .min(1, { message: "Zip is Required" })
    .max(10, { message: "Must be 10 or fewer characters long" }),
  appointmentNotes: z
    .string({ message: "Notes is Required" })
    .min(1, { message: "Notes is Required" })
    .max(250, { message: "Must be 250 or fewer characters long" }),
  appointmentPrepNotes: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .nullish(),
  appointmentPostNotes: z
    .string()
    .max(250, { message: "Must be 250 or fewer characters long" })
    .nullish(),
});

export default function AppointmentAddPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: userQueryData } = useUserQuery();
  const { data: careProvidersQueryData, isFetching } = useCareProvidersQuery(
    userQueryData?.patientUserId
  );
  const { mutate: appointmentPostMutate, isLoading: isLoadingPost } =
    useAppointmentPostMutation();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      appointmentDate: null,
      appointmentTime: null,
      providerUserId: "",
      appointmentFacility: "",
      appointmentAddress1: "",
      appointmentAddress2: "",
      appointmentCity: "",
      appointmentState: "",
      appointmentZip: "",
      appointmentNotes: "",
      appointmentPrepNotes: "",
      appointmentPostNotes: "",
    },
    resolver: zodResolver(postRequest),
  });
  const onSubmit = (formValues) => {
    appointmentPostMutate({
      patientUserId: userQueryData?.patientUserId,
      ...formValues,
      appointmentDate: formValues?.appointmentDate
        ?.toISOString()
        ?.split("T")[0],
      appointmentTime: formatTime(formValues?.appointmentTime),
    });
    navigate("/user/quick-actions/insights");
  };

  useEffect(() => {
    const hcp = searchParams.get("hcp");
    if (hcp && careProvidersQueryData) {
      const selectedProvider = careProvidersQueryData?.filter(
        (cp) => cp.id === hcp
      )[0];

      setValue("providerUserId", hcp);
      setValue(
        "appointmentFacility",
        selectedProvider?.providerHospitalName || ""
      );
      setValue(
        "appointmentAddress1",
        selectedProvider?.providerHospitalAddress1 || ""
      );
      setValue(
        "appointmentAddress2",
        selectedProvider?.providerHospitalAddress2 || ""
      );
      setValue("appointmentCity", selectedProvider?.providerHospitalCity || "");
      setValue(
        "appointmentState",
        selectedProvider?.providerHospitalState || ""
      );
      setValue("appointmentZip", selectedProvider?.providerHospitalZip || "");
    }
  }, [careProvidersQueryData, searchParams, setValue]);

  if (isFetching) return "Loading...";

  if (isLoadingPost) return "Saving...";

  if (careProvidersQueryData?.length <= 0) return <AppointmentEmptyProvider />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FlexFormContainer headerTitle={"Appointment Reminder"}>
        <Flex
          direction={{ base: "column", medium: "row" }}
        >
          <Controller
            name="appointmentDate"
            control={control}
            render={({ field }) => (
              <Flex direction="column">
                <Label htmlFor="appointmentDate">*Appointment Date</Label>
                <DatePicker
                  id={field.name}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  format="yyyy-MM-dd"
                  oneTap
                />
                {!!errors.appointmentDate && (
                  <Text
                    variation="error"
                    as="p"
                    lineHeight="0.5em"
                    fontWeight={400}
                    fontSize="0.8em"
                    fontStyle="normal"
                    textDecoration="none"
                    width="30vw"
                    style={{ marginLeft: "10px" }}
                  >
                    {errors.appointmentDate.message}
                  </Text>
                )}
              </Flex>
            )}
          />
          <Controller
            name="appointmentTime"
            control={control}
            render={({ field }) => (
              <Flex direction="column">
                <Label htmlFor="appointmentTime">*Appointment Time</Label>
                <DatePicker
                  id={field.name}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  format="hh:mm aa"
                  caretAs={FaRegClock}
                  showMeridian
                />
                {!!errors.appointmentTime && (
                  <Text
                    variation="error"
                    as="p"
                    lineHeight="0.5em"
                    fontWeight={400}
                    fontSize="0.8em"
                    fontStyle="normal"
                    textDecoration="none"
                    width="30vw"
                    style={{ marginLeft: "10px" }}
                  >
                    {errors.appointmentTime.message}
                  </Text>
                )}
              </Flex>
            )}
          />
        </Flex>

        <Controller
          name="providerUserId"
          control={control}
          render={({ field }) => (
            <SelectField
              size="small"
              minWidth="300px"
              label="*Provider"
              placeholder="Select"
              {...field}
              onChange={(e) => {
                const selectedProvider = careProvidersQueryData?.filter(
                  (cp) => cp.id === e.target.value
                )[0];
                setValue(
                  "appointmentFacility",
                  selectedProvider?.providerHospitalName || ""
                );
                setValue(
                  "appointmentAddress1",
                  selectedProvider?.providerHospitalAddress1 || ""
                );
                setValue(
                  "appointmentAddress2",
                  selectedProvider?.providerHospitalAddress2 || ""
                );
                setValue(
                  "appointmentCity",
                  selectedProvider?.providerHospitalCity || ""
                );
                setValue(
                  "appointmentState",
                  selectedProvider?.providerHospitalState || ""
                );
                setValue(
                  "appointmentZip",
                  selectedProvider?.providerHospitalZip || ""
                );
                field.onChange(e.target.value);
              }}
              errorMessage={errors.providerUserId?.message}
              hasError={!!errors.providerUserId}
            >
              {careProvidersQueryData?.map((cp) => (
                <option key={`aap_cp_${cp?.id}`} value={cp?.id}>
                  {cp?.providerName}
                </option>
              ))}
            </SelectField>
          )}
        />
        <Controller
          name="appointmentFacility"
          control={control}
          render={({ field }) => (
            <TextField
              size="small"
              minWidth="300px"
              label="Facility/Hospital Name"
              {...field}
              value={field.value}
              errorMessage={errors.appointmentFacility?.message}
              hasError={!!errors.appointmentFacility}
            />
          )}
        />
        <Flex direction={{ base: "column", medium: "row" }}>
          <Controller
            name="appointmentAddress1"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                minWidth="300px"
                label="Address line 1"
                {...field}
                value={field.value || ""}
                errorMessage={errors.appointmentAddress1?.message}
                hasError={!!errors.appointmentAddress1}
              />
            )}
          />
          <Controller
            name="appointmentAddress2"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                minWidth="300px"
                label="Address line 2"
                {...field}
                value={field.value || ""}
                errorMessage={errors.appointmentAddress2?.message}
                hasError={!!errors.appointmentAddress2}
              />
            )}
          />
        </Flex>
        <Flex direction={{ base: "column", medium: "row" }}>
          <Controller
            name="appointmentCity"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                minWidth="300px"
                label="City"
                {...field}
                value={field.value || ""}
                errorMessage={errors.appointmentCity?.message}
                hasError={!!errors.appointmentCity}
              />
            )}
          />
          <Controller
            name="appointmentState"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                minWidth="300px"
                label="State"
                {...field}
                value={field.value || ""}
                errorMessage={errors.appointmentState?.message}
                hasError={!!errors.appointmentState}
              />
            )}
          />
        </Flex>
        <Controller
          name="appointmentZip"
          control={control}
          render={({ field }) => (
            <TextField
              size="small"
              minWidth="300px"
              label="Zip code"
              {...field}
              value={field.value || ""}
              errorMessage={errors.appointmentZip?.message}
              hasError={!!errors.appointmentZip}
            />
          )}
        />

        <Flex
          direction="row"
        >
          <Controller
            name="appointmentNotes"
            control={control}
            render={({ field }) => (
              <TextAreaField
                size="small"
                minWidth="300px"
                label="Appointment Notes"
                {...field}
                value={field.value}
                errorMessage={errors.appointmentNotes?.message}
                hasError={!!errors.appointmentNotes}
              />
            )}
          />
        </Flex>
        <Divider size="small"/>
        <Heading>Prep Notes / Questions:</Heading>
        <Controller
          name="appointmentPrepNotes"
          control={control}
          render={({ field }) => (
            <TextAreaField
              size="small"
              minWidth="300px"
              rows={6}
              {...field}
              value={field.value}
              errorMessage={errors.appointmentPrepNotes?.message}
              hasError={!!errors.appointmentPrepNotes}
            />
          )}
        />
        <Heading>Post Notes / Answers:</Heading>

        <Controller
          name="appointmentPostNotes"
          control={control}
          render={({ field }) => (
            <TextAreaField
              size="small"
              minWidth="300px"
              rows={6}
              {...field}
              value={field.value}
              errorMessage={errors.appointmentPostNotes?.message}
              hasError={!!errors.appointmentPostNotes}
            />
          )}
        />
        <Flex gap="18px" direction="column">
          <Button variation="primary" type="submit" size="small">
            Save
          </Button>
        </Flex>
      </FlexFormContainer>
    </form>
  );
}
